import { useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";

import { RelativeTime } from "@/components/admin/RelativeTime";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import type { AdminAgentInstance } from "@/queries/adminAgents";
import { useUpdateAgentInstance } from "@/queries/adminAgents";

import { DisableInstanceDialog } from "./DisableInstanceDialog";
import { formatConfigValue } from "./utils";

interface InstanceRowProps {
  instance: AdminAgentInstance;
  isLastEnabledOfFamily: boolean;
}

export function InstanceRow({ instance, isLastEnabledOfFamily }: InstanceRowProps) {
  const { t } = useTranslation("admin-agents");
  const navigate = useNavigate();
  const mutation = useUpdateAgentInstance(instance.instance_id);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const parameterChips = Object.entries(instance.parameters);
  const overrideKeys = Object.keys(instance.overrides ?? {});

  const onToggle = (next: boolean) => {
    if (!instance.can_disable) return;
    if (instance.enabled && !next) {
      // Disabling — always confirm.
      setConfirmOpen(true);
      return;
    }
    mutation.mutate({ enabled: next });
  };

  const openDetail = () => {
    void navigate({
      to: "/admin/agents/$instanceId",
      params: { instanceId: instance.instance_id },
    });
  };

  return (
    <li
      data-testid={`instance-row-${instance.instance_id}`}
      className="flex flex-col gap-2 border-b py-3 last:border-b-0 sm:flex-row sm:items-center sm:gap-4"
    >
      <div className="flex items-center gap-2 sm:w-40">
        {instance.can_disable ? (
          <Switch
            aria-label={
              instance.enabled ? t("instance.enabledLabel") : t("instance.disabledLabel")
            }
            checked={instance.enabled}
            onCheckedChange={onToggle}
            disabled={mutation.isPending}
            data-testid={`instance-toggle-${instance.instance_id}`}
          />
        ) : (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Badge
                  variant="secondary"
                  data-testid={`instance-required-${instance.instance_id}`}
                >
                  {t("instance.required")}
                </Badge>
              </TooltipTrigger>
              <TooltipContent>{t("instance.requiredTooltip")}</TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
        <code className="rounded bg-muted px-1.5 py-0.5 text-xs font-mono">
          {instance.instance_id}
        </code>
      </div>

      <div className="flex flex-1 flex-wrap items-center gap-2 text-xs">
        {parameterChips.length === 0 ? (
          <span className="text-muted-foreground">{t("instance.noOverrides")}</span>
        ) : (
          parameterChips.map(([key, value]) => (
            <span
              key={key}
              className="rounded border border-dashed px-1.5 py-0.5 text-muted-foreground"
            >
              <span className="font-medium">{key}</span>: {formatConfigValue(value)}
            </span>
          ))
        )}
        {overrideKeys.length > 0 ? (
          <Badge
            variant="outline"
            data-testid={`instance-overrides-${instance.instance_id}`}
          >
            {t("instance.overrideBadge")} ({overrideKeys.length})
          </Badge>
        ) : null}
      </div>

      <div className="text-xs text-muted-foreground sm:w-32">
        <span className="mr-1">{t("instance.lastRun")}:</span>
        <RelativeTime
          iso={instance.last_run_at}
          fallbackKey="instance.lastRunNever"
          fallbackNs="admin-agents"
        />
      </div>

      <Button variant="ghost" size="sm" onClick={openDetail}>
        {t("instance.editOverrides")}
      </Button>

      <DisableInstanceDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        instance={instance}
        isLastEnabled={isLastEnabledOfFamily}
        onConfirm={() => {
          setConfirmOpen(false);
          mutation.mutate({ enabled: false });
        }}
      />
    </li>
  );
}
